import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import type { StructureRef } from '../../utils/proposalStructureRefs';
import {
  structureSelectionLabel,
  structureSelectionTitle,
  WHOLE_PROJECT_SELECTION_LABEL,
} from '../../utils/structureSelectionLabel';
import {
  selectedUploadStructureGroups,
  toggleUploadStructureGroup,
  type UploadStructureGroup,
} from '../../utils/projectFileStreamGroups';
import './StructureMultiSelect.css';
import './StructureCheckDropdown.css';

export interface StructureCheckDropdownProps {
  /** Structures on the proposal (the only place structures are born). */
  structures: StructureRef[];
  /** Selected structure ids; empty = whole project. */
  value: string[];
  onChange: (ids: string[]) => void;
  /**
   * When set, the menu lists these groups instead of individual structures.
   * Choosing a group selects every structure in it.
   */
  groups?: UploadStructureGroup[];
  placeholder?: string;
  disabled?: boolean;
  /** Tighter trigger for table-like rows. */
  compact?: boolean;
  /** Ids that cannot be picked here (already claimed by another target). */
  disabledIds?: ReadonlySet<string> | string[];
  /** Hide the "Whole project (clear)" row — e.g. when a selection is required. */
  hideClear?: boolean;
  className?: string;
  'aria-label'?: string;
  /** Extra line under the list (hint). */
  hint?: React.ReactNode;
  /** Keep the checklist open after each toggle (multi-pick without reopening). */
  keepOpenOnChange?: boolean;
}

type MenuPos = {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
};

const MENU_MIN_WIDTH = 220;
const MENU_EST_HEIGHT = 280;
const VIEWPORT_PAD = 8;

/**
 * Presentational multi-select for proposal structures: one trigger that reads
 * "Whole project" / "Barn" / "Barn + Pavilion" / "Barn + Pavilion +2", and a
 * checklist menu. No inline create — structures only come from the proposal.
 *
 * The menu is portaled to document.body with position:fixed so overflow on
 * upload-modal rows (and similar containers) cannot clip it.
 */
const StructureCheckDropdown: React.FC<StructureCheckDropdownProps> = ({
  structures,
  groups,
  value,
  onChange,
  placeholder,
  disabled = false,
  compact = false,
  disabledIds,
  hideClear = false,
  className = '',
  hint,
  keepOpenOnChange = false,
  ...rest
}) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listId = useId();

  const blocked = useMemo(() => {
    if (!disabledIds) return new Set<string>();
    return disabledIds instanceof Set ? disabledIds : new Set(disabledIds as string[]);
  }, [disabledIds]);

  const selected = useMemo(() => new Set(value), [value]);
  const groupMode = (groups?.length ?? 0) > 0;
  const chosenGroups = useMemo(
    () => (groupMode && groups ? selectedUploadStructureGroups(value, groups) : []),
    [groupMode, groups, value],
  );
  const groupCoversSelection = useMemo(() => {
    if (!groupMode || chosenGroups.length === 0) return false;
    const covered = new Set(chosenGroups.flatMap(group => group.structureIds));
    return value.length > 0 && value.every(id => covered.has(id));
  }, [groupMode, chosenGroups, value]);
  const selectedNames = useMemo(
    () => value.map(id => structures.find(s => s.id === id)?.name).filter((n): n is string => !!n),
    [value, structures],
  );
  const hasValue = value.length > 0;
  const label = groupCoversSelection
    ? structureSelectionLabel(chosenGroups.map(group => group.label))
    : hasValue
      ? structureSelectionLabel(selectedNames)
      : placeholder ?? WHOLE_PROJECT_SELECTION_LABEL;
  const title = groupCoversSelection
    ? chosenGroups.map(group => group.title || group.label).join(' · ')
    : structureSelectionTitle(selectedNames);
  const selectionCount = groupCoversSelection ? chosenGroups.length : value.length;

  const placeMenu = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const width = Math.max(rect.width, MENU_MIN_WIDTH);
    const measured = menuRef.current?.offsetHeight;
    const height = measured && measured > 0 ? measured : MENU_EST_HEIGHT;
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PAD;
    const spaceAbove = rect.top - VIEWPORT_PAD;
    const openUp = spaceBelow < Math.min(height, 200) && spaceAbove > spaceBelow;
    const maxHeight = Math.max(
      120,
      Math.min(MENU_EST_HEIGHT, openUp ? spaceAbove : spaceBelow, window.innerHeight * 0.5),
    );
    const left = Math.max(
      VIEWPORT_PAD,
      Math.min(rect.left, window.innerWidth - width - VIEWPORT_PAD),
    );
    const next: MenuPos = openUp
      ? { bottom: window.innerHeight - rect.top + 4, left, width, maxHeight }
      : { top: rect.bottom + 4, left, width, maxHeight };
    setMenuPos(prev =>
      prev
      && prev.top === next.top
      && prev.bottom === next.bottom
      && prev.left === next.left
      && prev.width === next.width
      && prev.maxHeight === next.maxHeight
        ? prev
        : next,
    );
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    placeMenu();
  }, [open, placeMenu, structures.length, groups?.length, hint, hideClear]);

  useLayoutEffect(() => {
    if (!open || !menuRef.current) return;
    placeMenu();
  }, [open, placeMenu, value.length]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (rootRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const onReposition = () => placeMenu();
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onReposition);
    document.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onReposition);
      document.removeEventListener('scroll', onReposition, true);
    };
  }, [open, placeMenu]);

  useEffect(() => {
    if (!open) return;
    // Focus the first option so arrow keys work straight away.
    const first = menuRef.current?.querySelector<HTMLElement>('[role="option"]');
    first?.focus();
  }, [open]);

  const toggle = useCallback(
    (id: string) => {
      if (blocked.has(id)) return;
      if (selected.has(id)) onChange(value.filter(v => v !== id));
      else onChange([...value, id]);
      if (keepOpenOnChange) setOpen(true);
    },
    [blocked, selected, value, onChange, keepOpenOnChange],
  );

  const pickOption = useCallback(
    (id: string, e: React.MouseEvent | React.KeyboardEvent) => {
      e.stopPropagation();
      if ('preventDefault' in e && e.type === 'click') e.preventDefault();
      toggle(id);
    },
    [toggle],
  );

  const moveFocus = (current: HTMLElement, dir: 1 | -1) => {
    const options = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? []);
    const idx = options.indexOf(current);
    if (idx < 0) return;
    const next = options[(idx + dir + options.length) % options.length];
    next?.focus();
  };

  const onOptionKey = (e: React.KeyboardEvent<HTMLElement>, act: () => void) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveFocus(e.currentTarget, 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveFocus(e.currentTarget, -1);
    } else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      act();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  const menu =
    open &&
    menuPos &&
    createPortal(
      <div
        ref={menuRef}
        id={listId}
        className="pm-structure-multi-menu pm-structure-multi-menu--portal"
        role="listbox"
        aria-multiselectable
        style={{
          top: menuPos.top,
          bottom: menuPos.bottom,
          left: menuPos.left,
          width: menuPos.width,
          maxHeight: menuPos.maxHeight,
        }}
        onMouseDown={
          keepOpenOnChange
            ? e => {
                e.preventDefault();
                e.stopPropagation();
              }
            : e => e.stopPropagation()
        }
      >
        <div className="pm-structure-multi-options">
          {!hideClear ? (
            <div
              role="option"
              tabIndex={0}
              aria-selected={!hasValue}
              className={`pm-structure-multi-option pm-structure-check-option pm-structure-check-option--whole${!hasValue ? ' is-selected' : ''}`}
              onClick={() => onChange([])}
              onKeyDown={e => onOptionKey(e, () => onChange([]))}
            >
              <span className="pm-structure-check-box" aria-hidden>
                {!hasValue ? <Check size={11} /> : null}
              </span>
              <span>{WHOLE_PROJECT_SELECTION_LABEL}</span>
              <span className="pm-structure-check-sub">{hasValue ? 'clear' : 'no structure tags'}</span>
            </div>
          ) : null}
          {groupMode ? (
            groups!.map(group => {
              const isSel = chosenGroups.some(chosen => chosen.id === group.id);
              return (
                <div
                  key={group.id}
                  role="option"
                  tabIndex={0}
                  aria-selected={isSel}
                  className={`pm-structure-multi-option pm-structure-check-option${isSel ? ' is-selected' : ''}`}
                  title={group.title}
                  onMouseDown={keepOpenOnChange ? e => e.stopPropagation() : undefined}
                  onClick={e => {
                    e.stopPropagation();
                    if (e.type === 'click') e.preventDefault();
                    onChange(toggleUploadStructureGroup(value, group.structureIds));
                    if (keepOpenOnChange) setOpen(true);
                  }}
                  onKeyDown={e => onOptionKey(e, () => {
                    onChange(toggleUploadStructureGroup(value, group.structureIds));
                    if (keepOpenOnChange) setOpen(true);
                  })}
                >
                  <span className="pm-structure-check-box" aria-hidden>
                    {isSel ? <Check size={11} /> : null}
                  </span>
                  <span>{group.label}</span>
                </div>
              );
            })
          ) : structures.length === 0 ? (
            <div className="pm-structure-multi-empty">
              No structures on this proposal yet. Add them in the Proposal Builder.
            </div>
          ) : (
            structures.map(st => {
              const isSel = selected.has(st.id);
              const isBlocked = blocked.has(st.id) && !isSel;
              return (
                <div
                  key={st.id}
                  role="option"
                  tabIndex={isBlocked ? -1 : 0}
                  aria-selected={isSel}
                  aria-disabled={isBlocked || undefined}
                  className={`pm-structure-multi-option pm-structure-check-option${isSel ? ' is-selected' : ''}${isBlocked ? ' is-blocked' : ''}`}
                  onMouseDown={keepOpenOnChange ? e => e.stopPropagation() : undefined}
                  onClick={e => pickOption(st.id, e)}
                  onKeyDown={e => onOptionKey(e, () => toggle(st.id))}
                  title={isBlocked ? 'Already covered by another track' : undefined}
                >
                  <span className="pm-structure-check-box" aria-hidden>
                    {isSel ? <Check size={11} /> : null}
                  </span>
                  <span>{st.name}</span>
                </div>
              );
            })
          )}
        </div>
        {hint ? <p className="pm-structure-multi-hint">{hint}</p> : null}
      </div>,
      document.body,
    );

  return (
    <div
      ref={rootRef}
      className={`pm-structure-multi pm-structure-check${open ? ' pm-structure-multi--open' : ''}${compact ? ' pm-structure-check--compact' : ''} ${className}`.trim()}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`pm-structure-multi-trigger${hasValue ? ' has-value' : ''}`}
        disabled={disabled}
        title={title}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={rest['aria-label']}
        onClick={() => setOpen(o => !o)}
        onKeyDown={e => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className="pm-structure-multi-trigger-label">{label}</span>
        {hasValue ? <span className="pm-structure-check-count">{selectionCount}</span> : null}
        <ChevronDown size={14} aria-hidden />
      </button>
      {menu}
    </div>
  );
};

export default StructureCheckDropdown;
